import { useState, useEffect, useCallback } from "react";
import { useAdminApi, fmtDate, useClasses } from "../adminUtils";
import "../admin-base.css";
import "../admin-layout.css";
import "../admin-controls.css";
import "../admin-students.css";
import "../admin-misc.css";
import "../admin-responsive.css";
import * as tus from "tus-js-client";

// ── Upload Section ────────────────────────────────────────
function UploadSection({ onUploaded }) {
  const { API, ADMIN_SECRET } = useAdminApi();
  const classes = useClasses();
  const [title, setTitle]     = useState("");
  const [teacher, setTeacher] = useState("");
  const [subject, setSubject] = useState("");
  const [source, setSource]   = useState("zoom_recording");
  const [classId, setClassId] = useState("");
  const [classDate, setClassDate] = useState("");
  const [file, setFile]       = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [error, setError]     = useState("");

  const handleUpload = async () => {
    if (!title) { setError("Title is required."); return; }
    if (!file)  { setError("Please choose a video file."); return; }
    setUploading(true); setError(""); setProgress(0);

    try {
      // Step 1: ask our server to create the Bunny placeholder + sign a
      // short-lived TUS upload authorization. No file bytes sent yet.
      const initRes = await fetch(`${API}/videos/init-upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${ADMIN_SECRET}` },
        body: JSON.stringify({
          title, teacher, subject, source,
          ...(classId ? { classId } : {}),
          ...(classDate ? { classDate } : {}),
        }),
      });
      const initData = await initRes.json();
      if (!initRes.ok) { setError(initData.error ?? "Could not start upload."); setUploading(false); return; }

      const { video, tusCredentials } = initData;

      // Step 2: upload the file directly to Bunny, browser → Bunny, no VPS relay.
      await new Promise((resolve, reject) => {
        const upload = new tus.Upload(file, {
          endpoint: tusCredentials.endpoint,
          retryDelays: [0, 3000, 5000, 10000, 20000],
          chunkSize: 50 * 1024 * 1024, // 50MB chunks
          headers: {
            AuthorizationSignature: tusCredentials.authorizationSignature,
            AuthorizationExpire: tusCredentials.authorizationExpire,
            VideoId: tusCredentials.videoId,
            LibraryId: tusCredentials.libraryId,
          },
          metadata: {
            filetype: file.type,
            title,
          },
          onError: (err) => reject(err),
          onProgress: (bytesUploaded, bytesTotal) => {
            setProgress(Math.round((bytesUploaded / bytesTotal) * 100));
          },
          onSuccess: () => resolve(),
        });
        upload.start();
      });

      // Step 3: tell our server the upload actually finished.
      await fetch(`${API}/videos/${video._id}/upload-complete`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${ADMIN_SECRET}` },
      });

      setTitle(""); setTeacher(""); setSubject(""); setFile(null); setProgress(0);
      onUploaded();
    } catch (err) {
      setError(err?.message || "Upload failed — check your connection and try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="adm-create-form" style={{ maxWidth: 480 }}>
      <p className="adm-form-title">Upload Recording</p>

      <div className="adm-field-group">
        <label className="adm-label">Title</label>
        <input className="adm-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Pranayama — Feb 1" />
      </div>

      <div className="adm-field-row">
        <div className="adm-field-group">
          <label className="adm-label">Teacher</label>
          <input className="adm-input" value={teacher} onChange={e => setTeacher(e.target.value)} />
        </div>
        <div className="adm-field-group">
          <label className="adm-label">Subject</label>
          <input className="adm-input" value={subject} onChange={e => setSubject(e.target.value)} />
        </div>
      </div>

      <div className="adm-field-group">
        <label className="adm-label">Source</label>
        <select className="adm-input" value={source} onChange={e => setSource(e.target.value)}>
          <option value="zoom_recording">Zoom Recording</option>
          <option value="direct_upload">Direct Upload</option>
        </select>
      </div>

      <div className="adm-field-row">
        <div className="adm-field-group">
          <label className="adm-label">Class (optional — links roster for Send)</label>
          <select className="adm-input" value={classId} onChange={e => setClassId(e.target.value)}>
            <option value="">— none —</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div className="adm-field-group">
          <label className="adm-label">Recorded on</label>
          <input className="adm-input" type="date" value={classDate} onChange={e => setClassDate(e.target.value)} />
        </div>
      </div>

      <div className="adm-field-group">
        <label className="adm-label">Video File</label>
        <input type="file" accept="video/*" onChange={e => setFile(e.target.files?.[0] ?? null)} />
      </div>

      {error && <p className="adm-error">{error}</p>}
      {uploading && (
        <p className="adm-loading">
          Uploading — {progress}% — this can take a while for large files, please don't close this tab…
        </p>
      )}

      <button className="adm-btn adm-btn--primary" onClick={handleUpload} disabled={uploading}>
        {uploading ? `Uploading… ${progress}%` : "Upload"}
      </button>
    </div>
  );
}

// ── Send Link Modal ───────────────────────────────────────
function SendModal({ video, onClose, onSent }) {
  const { API, ADMIN_SECRET } = useAdminApi();
  const [emails, setEmails]   = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError]     = useState("");
  const [result, setResult]   = useState(null);
  const [loadingRoster, setLoadingRoster] = useState(false);

  useEffect(() => {
    if (!video.classId || !video.classDate) return;
    setLoadingRoster(true);
    fetch(`${API}/videos/${video._id}/roster`, {
      headers: { Authorization: `Bearer ${ADMIN_SECRET}` },
    })
      .then(res => res.json())
      .then(data => {
        if (data.emails?.length > 0) setEmails(data.emails.join(", "));
      })
      .catch(() => {})
      .finally(() => setLoadingRoster(false));
  }, [video, API, ADMIN_SECRET]);

  const handleSend = async () => {
    const list = emails.split(",").map(e => e.trim()).filter(Boolean);
    if (list.length === 0) { setError("Enter at least one email address."); return; }
    setSending(true); setError(""); setResult(null);
    try {
      const res = await fetch(`${API}/videos/${video._id}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${ADMIN_SECRET}` },
        body: JSON.stringify({ emails: list }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? "Send failed."); return; }
      setResult(data);
      onSent();
    } catch {
      setError("Network error.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal" onClick={e => e.stopPropagation()}>
        <p className="adm-form-title">Send "{video.title}"</p>
        <div className="adm-field-group">
          <label className="adm-label">
            {video.classId ? "Class roster — edit if needed, email addresses separated by commas" : "Email addresses only, separated by commas — no names"}
          </label>
          {loadingRoster && <p className="adm-loading">Loading class roster…</p>}
          <textarea className="adm-input" rows={4} value={emails} onChange={e => setEmails(e.target.value)}
            placeholder="student1@example.com, student2@example.com" />
        </div>
        {error && <p className="adm-error">{error}</p>}
        {result && (
             <>
               {result.sent > 0 && (
                 <div style={{ background: "#d4f7e7", border: "1px solid #2e7d52", borderRadius: 6, padding: "0.75rem 1rem", marginBottom: result.failed?.length > 0 ? "0.5rem" : "0.75rem" }}>
                   <strong style={{ color: "#1a6640" }}>✓ Sent to {result.sent} student{result.sent === 1 ? "" : "s"}</strong>
                 </div>
               )}
               {result.failed?.length > 0 && (
                 <div style={{ background: "#fdf3e3", border: "1px solid #b97a0a", borderRadius: 6, padding: "0.75rem 1rem", marginBottom: "0.75rem" }}>
                   <strong style={{ color: "#8a5a00" }}>Not sent — not valid email addresses:</strong>
                   <div style={{ fontSize: "0.85rem", color: "#8a5a00", marginTop: "0.25rem" }}>{result.failed.join(", ")}</div>
                 </div>
               )}
             </>
           )}
        <div className="adm-modal-actions">
          <button className="adm-btn adm-btn--ghost" onClick={onClose}>Close</button>
          <button className="adm-btn adm-btn--primary" onClick={handleSend} disabled={sending}>
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function VideoListSection({ refreshKey }) {
  const { API, AUTH } = useAdminApi();
  const [videos, setVideos]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [sendTarget, setSendTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/videos/admin`, { headers: AUTH });
      const data = await res.json();
      setVideos(Array.isArray(data) ? data : []);
    } catch { setVideos([]); }
    finally   { setLoading(false); }
  }, [API, AUTH]);

  useEffect(() => { fetchVideos(); }, [fetchVideos, refreshKey]);

  const handleDelete = async (video) => {
    if (!window.confirm(`Delete "${video.title}"? This removes it from Bunny and cannot be undone.`)) return;
    setDeletingId(video._id);
    try {
      const res = await fetch(`${API}/videos/${video._id}`, {
        method: "DELETE",
        headers: AUTH,
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error ?? "Delete failed.");
        return;
      }
      setVideos(prev => prev.filter(v => v._id !== video._id));
    } catch {
      alert("Network error — delete failed.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDateChange = async (video, value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value === video.classDate) return;
    try {
      const res = await fetch(`${API}/videos/${video._id}/date`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...AUTH },
        body: JSON.stringify({ classDate: value }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error ?? "Could not update recording date.");
        return;
      }
      setVideos(prev => prev.map(v => v._id === video._id ? data : v));
    } catch {
      alert("Network error — could not update recording date.");
    }
  };

  // newest first — classDate (recording date) falls back to createdAt
  const sortedVideos = [...videos].sort((a, b) =>
    (b.classDate || b.createdAt || "").localeCompare(a.classDate || a.createdAt || "")
  );

  const handleTogglePublish = async (video) => {
    setTogglingId(video._id);
    try {
      const res = await fetch(`${API}/videos/${video._id}/publish`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...AUTH },
        body: JSON.stringify({ isPublished: !video.isPublished }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error ?? "Could not update publish status.");
        return;
      }
      setVideos(prev => prev.map(v => v._id === video._id ? data : v));
    } catch {
      alert("Network error — could not update publish status.");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div>
      <div className="adm-roster-header" style={{ marginTop: "2rem" }}>
        <h2 className="adm-section-title">Recordings</h2>
        <span className="adm-count-badge">{videos.length}</span>
      </div>

      {loading && <p className="adm-loading">Loading…</p>}
      {!loading && videos.length === 0 && <p className="adm-empty">No recordings uploaded yet.</p>}
      {!loading && videos.length > 0 && (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr><th>Title</th><th>Teacher</th><th>Source</th><th>Recorded</th><th>Uploaded</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {sortedVideos.map(v => (
                <tr key={v._id}>
                  <td><strong>{v.title}</strong></td>
                  <td>{v.teacher || "—"}</td>
                  <td><span className="adm-type-badge">{v.source === "zoom_recording" ? "Zoom" : "Upload"}</span></td>
                  <td>
                    <input
                      key={`${v._id}-${v.classDate || ""}`}
                      className="adm-input"
                      type="date"
                      defaultValue={v.classDate || ""}
                      onChange={e => handleDateChange(v, e.target.value)}
                    />
                  </td>
                  <td>{fmtDate(v.createdAt)}</td>
                  <td>
                    <span className={`adm-status-badge ${v.isPublished ? "status-published" : "status-unpublished"}`}>
                      {v.isPublished ? "Published" : "Unpublished"}
                    </span>
                  </td>
                  <td>
                  <div className="adm-row-actions">
                    <button
                      className="adm-btn-ghost-sm"
                      onClick={() => handleTogglePublish(v)}
                      disabled={togglingId === v._id}
                    >
                      {togglingId === v._id ? "…" : v.isPublished ? "Unpublish" : "Publish"}
                    </button>
                    <button className="adm-btn-ghost-sm" onClick={() => setSendTarget(v)}>Send Link</button>
                    <button
                      className="adm-btn-danger-sm"
                      onClick={() => handleDelete(v)}
                      disabled={deletingId === v._id}
                    >
                      {deletingId === v._id ? "Deleting…" : "Delete"}
                    </button>
                  </div>
                </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sendTarget && (
        <SendModal
          video={sendTarget}
          onClose={() => setSendTarget(null)}
          onSent={() => {}}
        />
      )}
    </div>
  );
}

// ── Main Tab ──────────────────────────────────────────────
export default function VideosTab() {
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="adm-tab-content">
      <UploadSection onUploaded={() => setRefreshKey(k => k + 1)} />
      <VideoListSection refreshKey={refreshKey} />
    </div>
  );
}